import { cn } from '@/utils/cn'
import { Typography } from '@/utils/styles'

export interface SectionHeaderProps {
  title: string
  subtitle?: string
  align?: 'left' | 'center' | 'right'
  marginBottom?: 'sm' | 'md' | 'lg' | 'xl' // mb-8, mb-12, mb-16, mb-20
  titleSize?: 'md' | 'lg' | 'xl'
  badge?: string // Optional badge text above title
  dark?: boolean // For dark backgrounds (white text)
  className?: string
}

export function SectionHeader({
  title,
  subtitle,
  align = 'center',
  marginBottom = 'lg',
  titleSize = 'lg',
  badge,
  dark = false,
  className,
}: SectionHeaderProps): HTMLElement {
  // Alignment classes
  const alignments = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }

  // Margin bottom variants
  const margins = {
    sm: 'mb-8',
    md: 'mb-12',
    lg: 'mb-16',
    xl: 'mb-20',
  }

  // Title size variants
  const titleSizes = {
    md: Typography.heading.h3,
    lg: Typography.heading.h2,
    xl: Typography.heading.h1,
  }

  const container = document.createElement('div')
  container.className = cn(
    alignments[align],
    margins[marginBottom],
    className
  )

  // Badge (optional, appears above title)
  if (badge) {
    const badgeElement = document.createElement('div')
    badgeElement.className = cn(
      Typography.label.md,
      dark ? 'text-orange-400' : 'text-orange-600',
      'mb-3'
    )
    badgeElement.textContent = badge
    container.appendChild(badgeElement)
  }

  // Title
  const titleElement = document.createElement('h2')
  titleElement.className = cn(
    titleSizes[titleSize],
    dark ? 'text-white' : 'text-slate-900',
    'mb-4'
  )
  titleElement.textContent = title

  // Subtitle (optional)
  if (subtitle) {
    const subtitleElement = document.createElement('p')
    subtitleElement.className = cn(
      Typography.body.lg,
      dark ? 'text-slate-300' : 'text-slate-600',
      'max-w-3xl',
      align === 'center' && 'mx-auto',
      align === 'right' && 'ml-auto'
    )
    subtitleElement.textContent = subtitle

    container.appendChild(titleElement)
    container.appendChild(subtitleElement)
  } else {
    container.appendChild(titleElement)
  }

  return container
}
